import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkle, FloppyDisk, ArrowClockwise, BookOpen, CheckCircle, Star } from "@phosphor-icons/react";
import SaveToHouseVoiceDialog from "./SaveToHouseVoiceDialog";

/**
 * Engagement closeout dialog — Yadid drafts a five-section lessons-learned memo
 * the partner reviews, edits, and files to the firm library. Non-destructive:
 * re-drafting overwrites the in-memory state only; save writes to firm_library.
 */
export default function CloseoutMemoDialog({ engagement, open, onOpenChange, onSaved }) {
  const [memo, setMemo] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alreadyFiled, setAlreadyFiled] = useState(null);
  const [preSaveMemo, setPreSaveMemo] = useState(null); // snapshot right before filing
  const [voiceCaptureOpen, setVoiceCaptureOpen] = useState(false);
  const [voiceSeed, setVoiceSeed] = useState({ original: "", approved: "" });

  useEffect(() => {
    if (!open || !engagement?.id) return;
    setMemo(null);
    setAlreadyFiled(null);
    // Priority: already-filed memo > pending auto-draft (from status→closed hook).
    api.get(`/engagements/${engagement.id}/closeout`)
      .then((r) => { setAlreadyFiled(r.data); setMemo(r.data); })
      .catch(async () => {
        // No filed memo — is there a pending auto-draft waiting?
        try {
          const { data } = await api.get(`/engagements/${engagement.id}/closeout/pending`);
          setMemo(data);
          toast.success("Auto-drafted when this engagement closed — review, edit, then file");
        } catch (e) {
          // 404 is the expected path (neither filed memo nor pending draft exists —
          // the dialog will show the manual draft CTA). Log anything else so a
          // real failure (500, network) is visible in the console.
          if (e?.response?.status !== 404) {
            console.warn("closeout/pending fetch failed", e);
          }
        }
      });
  }, [open, engagement?.id]);

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/engagements/${engagement.id}/closeout/draft`);
      setMemo(data);
      // Snapshot the freshly-drafted memo so we can diff against the partner's
      // final approved version when they later opt to save it as a house-voice
      // example. Captured BEFORE any edits happen.
      setPreSaveMemo(JSON.parse(JSON.stringify(data)));
      toast.success("YadidAi drafted a memo — review, edit, then file it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setDrafting(false); }
  };

  const openHouseVoiceCapture = () => {
    // Prefer the freshly-drafted snapshot; fall back to the filed memo as-is
    // (partner may reopen an already-filed memo and just want to save a bit of it).
    const orig = preSaveMemo || alreadyFiled || memo;
    const bodyOf = (m) => (m?.sections || []).map((s) => `${s.label}:\n${s.body || ""}`).join("\n\n");
    setVoiceSeed({ original: bodyOf(orig), approved: bodyOf(memo) });
    setVoiceCaptureOpen(true);
  };

  const patchSection = (idx, body) => {
    setMemo((m) => ({ ...m, sections: m.sections.map((s, i) => (i === idx ? { ...s, body } : s)) }));
  };

  const save = async () => {
    if (!memo) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/engagements/${engagement.id}/closeout`, {
        title: memo.title,
        subtitle: memo.subtitle || "",
        sections: memo.sections.map((s) => ({ key: s.key, label: s.label, body: s.body || "" })),
      });
      toast.success("Filed to the firm library");
      setAlreadyFiled(data);
      if (onSaved) onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="closeout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BookOpen size={16} weight="fill" className="text-primary" />
            Closeout memo · {engagement?.name}
          </DialogTitle>
        </DialogHeader>

        {alreadyFiled && !drafting && (
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow flex items-center gap-1.5">
            <CheckCircle size={12} weight="fill" /> Filed {alreadyFiled.filed_at?.slice(0, 10)} · by {alreadyFiled.filed_by_name || alreadyFiled.filed_by_email}
          </div>
        )}

        {!memo && !drafting && (
          <div className="border border-dashed border-primary/40 p-6 text-center">
            <div className="text-sm text-muted-foreground mb-4">
              YadidAi will read every meeting, decision, and playbook edit on this engagement and draft a five-section lessons-learned memo you can review, edit, and file to the firm library.
            </div>
            <Button onClick={draft} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" data-testid="closeout-draft-btn">
              <Sparkle size={14} weight="fill" className="mr-2" /> Draft memo with YadidAi
            </Button>
          </div>
        )}

        {drafting && <div className="text-sm text-muted-foreground italic">Reading history and drafting… (this can take 20-40 seconds).</div>}

        {memo && (
          <div className="space-y-4">
            <div>
              <label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Title</label>
              <Input value={memo.title || ""} onChange={(e) => setMemo((m) => ({ ...m, title: e.target.value }))} className="rounded-none bg-secondary/40 mt-1 font-display text-base" />
            </div>
            <div>
              <label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Subtitle</label>
              <Input value={memo.subtitle || ""} onChange={(e) => setMemo((m) => ({ ...m, subtitle: e.target.value }))} className="rounded-none bg-secondary/40 mt-1" />
            </div>
            {memo.sections.map((s, i) => (
              <div key={s.key} className="border-l-2 border-primary/40 pl-4" data-testid={`closeout-section-${s.key}`}>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-1">{s.label}</div>
                <Textarea
                  rows={5}
                  value={s.body || ""}
                  onChange={(e) => patchSection(i, e.target.value)}
                  data-testid={`closeout-body-${s.key}`}
                  className="rounded-none bg-secondary/40 text-sm"
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {memo && !drafting && (
            <>
              <Button onClick={draft} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10" data-testid="closeout-redraft-btn">
                <ArrowClockwise size={14} className="mr-2" /> Re-draft
              </Button>
              {alreadyFiled && (
                <Button onClick={openHouseVoiceCapture} variant="outline"
                        className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
                        data-testid="closeout-house-voice-btn">
                  <Star size={14} weight="fill" className="mr-2" /> Save to house voice
                </Button>
              )}
              <Button onClick={save} disabled={saving} className="rounded-none bg-primary text-primary-foreground" data-testid="closeout-save-btn">
                <FloppyDisk size={14} weight="bold" className="mr-2" /> {saving ? "Filing…" : "File to firm library"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <SaveToHouseVoiceDialog
        open={voiceCaptureOpen}
        onOpenChange={setVoiceCaptureOpen}
        sourceKind="closeout"
        originalText={voiceSeed.original}
        approvedText={voiceSeed.approved}
        engagementId={engagement?.id}
        clientId={engagement?.client_id}
        engagementType={engagement?.engagement_type}
      />
    </Dialog>
  );
}
